import { useState, useEffect } from 'react';
import { Paper, Box, Stack, Button, ToggleButton, ToggleButtonGroup } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { FileDownloadOutlined, RefreshOutlined } from '@mui/icons-material';
import { toast } from 'react-hot-toast';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,

} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import { useExportCaseAnalytics } from '../../../../../../../../features/analytics/hooks/exports';
import { useGetCaseAnalyticsRange } from '../../../../../../../../features/analytics/hooks/useGetCaseAnalytics';
import FullScreenLoader from '../../../../../../caller/components/fullscreen-loader';
import { DeliveredBackground, DeliveredBorder, NewRegistrationBackground, NewRegistrationBorder } from '../../utility/graph';
import dayjs from 'dayjs';
import DateRangePicker from '../../../../components/date-range-picker';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

export const CaseAnalyticsChart = ({ fromDate: controlledFromDate, toDate: controlledToDate, onFromDateChange, onToDateChange }) => {
  const [localFromDate, setLocalFromDate] = useState(dayjs().startOf('month'));
  const [localToDate, setLocalToDate] = useState(dayjs().endOf('month'));
  const [viewMode, setViewMode] = useState('count'); // 'count' or 'percentage'

  const fromDate = controlledFromDate !== undefined ? controlledFromDate : localFromDate;
  const toDate = controlledToDate !== undefined ? controlledToDate : localToDate;

  useEffect(() => {
    if (controlledFromDate !== undefined) {
      setLocalFromDate(controlledFromDate);
    }
  }, [controlledFromDate]);

  useEffect(() => {
    if (controlledToDate !== undefined) {
      setLocalToDate(controlledToDate);
    }
  }, [controlledToDate]);

  const handleFromDateChange = (newDate) => {
    setLocalFromDate(newDate);
  };

  const handleToDateChange = (newDate) => {
    setLocalToDate(newDate);
  };

  const handleViewModeChange = (event, newViewMode) => {
    if (newViewMode !== null) {
      setViewMode(newViewMode);
    }
  };

  // Always refetch on mount by using a unique key (forces remount/fetch)
  const [fetchKey] = useState(() => Date.now());
  const { data: caseAnalytics, isFetching, refetch } = useGetCaseAnalyticsRange(
    // fromDate,
    // toDate,
    localFromDate,
    localToDate,
    fetchKey
  );

  // Export hook with UX callbacks
  const exportCaseAnalytics = useExportCaseAnalytics({
    onSuccess: (data) => {
      toast.success(`Case analytics exported successfully! File: ${data.filename}`);
    },
    onError: (error) => {
      toast.error(`Export failed: ${error.message}`);
    }
  });

  // Handle export button click
  const handleExport = () => {
    const exportParams = {
      // year: localSelectedDate.year(),
      // month: localSelectedDate.month() + 1,
      from: fromDate.format('YYYY-MM-DD'),
      to: toDate.format('YYYY-MM-DD'),
    };

    toast.loading('Preparing export...', { id: 'case-analytics-export' });

    exportCaseAnalytics.mutate(exportParams, {
      onSettled: () => {
        toast.dismiss('case-analytics-export');
      }
    });
  };

  // Handle refresh button click
  const handleRefresh = () => {
    refetch();
  };


  // Get data for selected date range
  const getSelectedData = () => {
    const data = caseAnalytics?.data || {
      newRegistration: 0,
      delivered: 0,
      newRegistrationPercentage: 0,
      deliveredPercentage: 0
    };

    const isPercentageView = viewMode === 'percentage';
    const chartData = isPercentageView
      ? [data.newRegistrationPercentage, data.deliveredPercentage]
      : [data.newRegistration, data.delivered];

    const dataLabel = isPercentageView ? 'Percentage' : 'Count';

    return {
      labels: ['New Registration', 'Delivered'],
      datasets: [
        {
          label: `${fromDate.format('MMM YYYY')} - ${toDate.format('MMM YYYY')} ${dataLabel}`,
          data: chartData,
          backgroundColor: [
            NewRegistrationBackground,
            DeliveredBackground
          ],
          borderColor: [
            NewRegistrationBorder,
            DeliveredBorder
          ],
          borderWidth: 1,
        }
      ]
    };
  };

  const chartOptions = {
    indexAxis: 'y',
    responsive: true,
    plugins: {
      legend: {
        position: 'bottom',
        display: true,
        onClick: null,
        onHover: (e) => { e.native.target.style.cursor = 'default'; },
        labels: {
          usePointStyle: true,
          pointStyle: 'rect',
          padding: 20,
          font: {
            size: 14,
            weight: 'bold'
          },
          color: '#333',
          generateLabels: function (chart) {
            const data = chart.data;
            if (data.datasets.length) {
              return data.labels.map((label, i) => {
                const dataset = data.datasets[0];
                return {
                  text: label,
                  fillStyle: dataset.backgroundColor[i],
                  strokeStyle: dataset.borderColor[i],
                  lineWidth: 2,
                  hidden: false,
                  index: i,
                  pointStyle: 'rect'
                };
              });
            }
            return [];
          }
        }
      },
      title: {
        display: true,
        text: `Case Analytics - ${fromDate.format('MMM YYYY')} to ${toDate.format('MMM YYYY')} (${viewMode === 'percentage' ? 'Percentage' : 'Count'})`,
        font: {
          size: 16,
        }
      },
      tooltip: {
        callbacks: {
          label: function (context) {
            const label = context.dataset.label || '';
            const value = context.parsed.x;
            return viewMode === 'percentage'
              ? `${label}: ${value}%`
              : `${label}: ${value}`;
          }
        }
      },
      // This configures the number you see inside the bar
      datalabels: {
        color: '#FFFFFF',
        font: {
          weight: 'bold',
          size: 24
        }
      }
    },
    scales: {
      x: {
        beginAtZero: true,
        grid: {
          display: true,
        },
        ticks: {
          stepSize: viewMode === 'percentage' ? 10 : 200,
          callback: function (value) {
            return viewMode === 'percentage' ? `${value}%` : value;
          }
        }
      },
      y: {
        grid: {
          display: false,
        }
      }
    },
    maintainAspectRatio: false,
  };

  return (
    <Paper elevation={2} sx={{ p: 2, mb: 2, border: '1px solid #d0d0d0' }}>
      <FullScreenLoader open={isFetching} />
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <DateRangePicker
          fromDate={fromDate}
          toDate={toDate}
          onFromChange={handleFromDateChange}
          onToChange={handleToDateChange}
          disabled={isFetching || exportCaseAnalytics.isLoading}
        />
        <ToggleButtonGroup
          value={viewMode}
          exclusive
          onChange={handleViewModeChange}
          size="small"
          disabled={isFetching || exportCaseAnalytics.isLoading}
        >
          <ToggleButton value="count">
            Count
          </ToggleButton>
          <ToggleButton value="percentage">
            Percentage
          </ToggleButton>
        </ToggleButtonGroup>
        <Stack direction="row" spacing={1}>
          <Button
            variant="outlined"
            startIcon={<RefreshOutlined />}
            onClick={handleRefresh}
            disabled={isFetching || exportCaseAnalytics.isLoading}
            size="small"
            sx={{
              borderColor: '#1976d2',
              color: '#1976d2',
              '&:hover': {
                borderColor: '#1565c0',
                backgroundColor: '#f3f4f6',
              },
              '&:disabled': {
                borderColor: '#e0e0e0',
                color: '#9e9e9e',
              }
            }}
          >
            {isFetching ? 'Refreshing...' : 'Refresh'}
          </Button>
          <Button
            variant="outlined"
            startIcon={<FileDownloadOutlined />}
            onClick={handleExport}
            disabled={isFetching || exportCaseAnalytics.isLoading || !caseAnalytics?.data}
            size="small"
            sx={{
              borderColor: '#1976d2',
              color: '#1976d2',
              '&:hover': {
                borderColor: '#1565c0',
                backgroundColor: '#f3f4f6',
              },
              '&:disabled': {
                borderColor: '#e0e0e0',
                color: '#9e9e9e',
              }
            }}
          >
            {exportCaseAnalytics.isLoading ? 'Exporting...' : 'Export'}
          </Button>
        </Stack>
      </Stack>
      <Box sx={{ height: 400, width: '100%' }}>
        <Bar data={getSelectedData()} options={chartOptions} />
      </Box>
    </Paper>
  );
};
